import { Dispatch, PropsWithChildren, SetStateAction, useCallback, useMemo, useState } from 'react';
import { createContext, useContext } from 'react';

export interface StepperContextProps<T extends readonly string[], K> {
  steps: T;
  initialStep?: string;
  step: string;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: T[number]) => void;
  contextData: Partial<K> | null;
  setContextData: Dispatch<SetStateAction<Partial<K>>>;
  contextErrors: ContextErrors;
  setContextError: (key: string, error: string) => void;
  clearContextErrors: () => void;
  currentStepIndex: number;
  hasNextStep: boolean;
  hasPreviousStep: boolean;
  resetStepper: () => void;
  getNextStep: () => T[number] | null;
  isLoadingStep: boolean;
}

export type StepperProviderProps<T extends readonly string[]> = {
  steps: T;
  initialStep?: T[number];
  isLoadingStep?: boolean;
  persistKey?: string;
};

const StepperContext = createContext<StepperContextProps<readonly string[], any>>(undefined!);

type ContextErrors = {
  [key: string]: string[];
};

function readFromSession<T>(key: string, fallback: T): T {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeToSession(key: string, value: unknown) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

export const StepperContextProvider = <T extends readonly string[]>({
  children,
  steps,
  initialStep,
  isLoadingStep = false,
  persistKey,
}: PropsWithChildren<StepperProviderProps<T>>) => {
  const [contextData, setContextDataRaw] = useState<any>(() =>
    persistKey ? readFromSession(`${persistKey}:data`, null) : null
  );
  const [contextErrors, setContextErrors] = useState<ContextErrors>({});
  const [step, setStepRaw] = useState<T[number]>(() =>
    persistKey
      ? readFromSession(`${persistKey}:step`, initialStep ?? steps[0])
      : (initialStep ?? steps[0])
  );

  const setStep = useCallback(
    (value: T[number]) => {
      setStepRaw(value);
      if (persistKey) writeToSession(`${persistKey}:step`, value);
    },
    [persistKey]
  );

  const setContextData: Dispatch<SetStateAction<Partial<any>>> = useCallback(
    (value) => {
      setContextDataRaw((prev: any) => {
        const next = typeof value === 'function' ? value(prev) : value;
        if (persistKey) writeToSession(`${persistKey}:data`, next);
        return next;
      });
    },
    [persistKey]
  );

  const currentStepIndex = useMemo(() => steps.indexOf(step), [step, steps]);
  const hasNextStep = useMemo(() => currentStepIndex < steps.length - 1, [currentStepIndex, steps]);
  const hasPreviousStep = useMemo(() => currentStepIndex > 0, [currentStepIndex]);

  const setContextError = useCallback((key: string, error: string) => {
    setContextErrors((prev) => ({
      ...prev,
      [key]: prev[key] ? [...prev[key], error] : [error],
    }));
  }, []);

  const clearContextErrors = useCallback(() => {
    setContextErrors({});
  }, []);

  const nextStep = useCallback(() => {
    if (hasNextStep) {
      setStep(steps[currentStepIndex + 1]);
    }
  }, [currentStepIndex, hasNextStep, steps, setStep]);

  const getNextStep = useCallback(() => {
    if (hasNextStep) {
      return steps[currentStepIndex + 1];
    }

    return null;
  }, [currentStepIndex, hasNextStep, steps]);

  const previousStep = useCallback(() => {
    if (hasPreviousStep) {
      setStep(steps[currentStepIndex - 1]);
    }
  }, [currentStepIndex, hasPreviousStep, steps, setStep]);

  const goToStep = useCallback(
    (s: T[number]) => {
      if (steps.includes(s)) {
        setStep(s);
      }
    },
    [setStep, steps]
  );

  const resetStepper = useCallback(() => {
    goToStep(steps[0]);
    setContextDataRaw(null);
    clearContextErrors();
    if (persistKey) {
      try {
        sessionStorage.removeItem(`${persistKey}:step`);
        sessionStorage.removeItem(`${persistKey}:data`);
      } catch {
        // ignore
      }
    }
  }, [clearContextErrors, goToStep, steps, setContextData, persistKey]);

  return (
    <StepperContext.Provider
      value={{
        steps,
        initialStep,
        step,
        nextStep,
        previousStep,
        goToStep,
        contextData,
        setContextData,
        contextErrors,
        setContextError,
        clearContextErrors,
        currentStepIndex,
        hasNextStep,
        hasPreviousStep,
        resetStepper,
        getNextStep,
        isLoadingStep,
      }}
    >
      {children}
    </StepperContext.Provider>
  );
};

export function useStepperContext<T extends readonly string[], K>(): StepperContextProps<T, K> {
  const context = useContext(StepperContext);

  if (typeof context === 'undefined') {
    throw new Error('useStepperContext should be used within the StepperContext provider!');
  }

  return context as unknown as StepperContextProps<T, K>;
}
